/**
 * @license
 * Copyright 2026 NeuroSimplicity
 * Licensed under the Apache License, Version 2.0
 */

import {getBoundingBoxCenter} from 'neuroglancer/coordinate_transform';
import {UserLayer} from 'neuroglancer/layer';
import {SegmentationUserLayer} from 'neuroglancer/segmentation_user_layer';
import {Viewer} from 'neuroglancer/viewer';
import {kAxes} from 'neuroglancer/util/geom';
import {parseFiniteVec} from 'neuroglancer/util/json';
import {Uint64} from 'neuroglancer/util/uint64';

type ParentCommandMessage = {
  type: 'NEUROSIMP_COMMAND';
  command: string;
  payload?: Record<string, unknown>;
};

/** Same math as perspective_view/panel rotate-via-mouse-drag. */
function simulateDrag(viewer: Viewer, deltaX: number, deltaY: number) {
  const {pose} = viewer.perspectiveNavigationState;
  pose.rotateRelative(kAxes[1], (deltaX / 4.0) * Math.PI / 180.0);
  pose.rotateRelative(kAxes[0], (-deltaY / 4.0) * Math.PI / 180.0);
}

function setCamera(viewer: Viewer, payload: Record<string, unknown>) {
  if (payload.projectionScale !== undefined) {
    const scale = Number(payload.projectionScale);
    if (Number.isFinite(scale) && scale > 0) {
      // Match URL hash `projectionScale` — restoreState, not legacyValue.
      viewer.projectionScale.restoreState(scale);
    }
  }

  if (Array.isArray(payload.position)) {
    const coords = new Float32Array(payload.position.length);
    try {
      parseFiniteVec(coords, payload.position);
      viewer.position.value = coords;
    } catch {
      // Ignore malformed position payloads.
    }
  }

  if (Array.isArray(payload.projectionOrientation)) {
    viewer.projectionOrientation.restoreState(payload.projectionOrientation);
  }
}

/**
 * Toggle existing layers in place. Unlike a hash update (which clears and
 * recreates every layer, refetching meshes), this keeps loaded data and the
 * camera pose. Layers must already exist in the state; unknown names are ignored.
 */
function setLayerVisibility(viewer: Viewer, payload: Record<string, unknown>) {
  const layers = payload.layers;
  if (!layers || typeof layers !== 'object') {
    return;
  }
  for (const [name, visible] of Object.entries(layers as Record<string, unknown>)) {
    const managedLayer = viewer.layerManager.getLayerByName(name);
    if (managedLayer !== undefined && typeof visible === 'boolean') {
      managedLayer.setVisible(visible);
    }
  }
}

/** Same field as the `projectionDepth` URL-hash key (negative = clip depth). */
function setProjectionDepth(viewer: Viewer, payload: Record<string, unknown>) {
  const value = Number(payload.value);
  if (!Number.isFinite(value)) {
    return;
  }
  if (value >= 0) {
    viewer.projectionDepthRange.reset();
    return;
  }
  viewer.projectionDepthRange.value = value;
}

/** Legacy imaging-suite clip slider: `value` is 0 (no clip) … 1 (full clip). */
function updateClippingPlane(viewer: Viewer, payload: Record<string, unknown>) {
  const clip = Number(payload.value);
  if (!Number.isFinite(clip) || clip <= 0) {
    viewer.projectionDepthRange.reset();
    return;
  }
  viewer.projectionDepthRange.value = -clip;
}

/**
 * Spatial center of a loaded data source, in that source's output space.
 * `localCoordinateSpace` only keeps dimensions named with `'` / `^`, so x/y/z
 * never appear there and a focus based on it orbits the wrong point.
 */
function loadedOutputSpace(userLayer: UserLayer) {
  for (const source of userLayer.dataSources) {
    const loaded = source.loadState;
    if (loaded === undefined || loaded.error !== undefined) continue;
    const output = loaded.transform.value.outputSpace;
    if (output.valid && output.rank >= 3) return output;
  }
  return undefined;
}

/**
 * Move the camera onto one layer in the shared global voxel grid.
 * Output-space bounds and scales are one pair (25 µm light-sheet vs 13.5 µm
 * micro-CT), so the orbit point stays at the volume center.
 */
function focusLayer(viewer: Viewer, payload: Record<string, unknown>) {
  const layerName = typeof payload.layerName === 'string' ? payload.layerName : '';
  const userLayer = layerName ? viewer.layerManager.getLayerByName(layerName)?.layer : undefined;

  if (!Array.isArray(payload.position) && userLayer != null) {
    const outputSpace = loadedOutputSpace(userLayer);
    const globalSpace = viewer.coordinateSpace.value;
    if (outputSpace !== undefined && globalSpace.valid) {
      const globalPos = new Float32Array(globalSpace.rank);
      let maxExtent = 0;
      let matched = 0;
      for (let outputDim = 0; outputDim < outputSpace.rank; ++outputDim) {
        const name = outputSpace.names[outputDim];
        if (name.endsWith('\'') || name.endsWith('^')) continue;
        const globalDim = globalSpace.names.indexOf(name);
        if (globalDim < 0) continue;
        const lower = outputSpace.bounds.lowerBounds[outputDim];
        const upper = outputSpace.bounds.upperBounds[outputDim];
        if (!Number.isFinite(lower) || !Number.isFinite(upper) || upper <= lower) continue;
        const outputScale = outputSpace.scales[outputDim] || 1;
        const globalScale = globalSpace.scales[globalDim] || 1;
        const ratio = globalScale !== 0 ? outputScale / globalScale : 1;
        globalPos[globalDim] = Math.floor(((lower + upper) / 2) * ratio) + 0.5;
        maxExtent = Math.max(maxExtent, (upper - lower) * ratio);
        ++matched;
      }
      if (matched >= 3) {
        viewer.position.value = globalPos;
        if (payload.projectionScale === undefined && maxExtent > 0) {
          // Focal-plane height. Below ~1x the longest axis the eye sits inside
          // the mesh and the view goes blank.
          viewer.projectionScale.restoreState(Math.max(500, maxExtent * 1.45));
        }
      }
    }
  }

  // Orientation only. Do not move `position` afterward — drag rotation orbits
  // that point, and it has to stay inside the specimen.
  if (Array.isArray(payload.position) || payload.projectionScale !== undefined ||
      Array.isArray(payload.projectionOrientation)) {
    const cameraPayload: Record<string, unknown> = {};
    if (Array.isArray(payload.position)) cameraPayload.position = payload.position;
    if (payload.projectionScale !== undefined) cameraPayload.projectionScale = payload.projectionScale;
    if (Array.isArray(payload.projectionOrientation)) {
      cameraPayload.projectionOrientation = payload.projectionOrientation;
    }
    setCamera(viewer, cameraPayload);
  }
}

/** Frame the named layers (or all data bounds if names omitted). */
function centerOnLayers(viewer: Viewer, payload: Record<string, unknown>) {
  if (Array.isArray(payload.position)) {
    setCamera(viewer, payload);
    return;
  }

  const layerNames = payload.layerNames;
  const names = Array.isArray(layerNames) ? layerNames.map(String) : [];

  let centeredFromLayer = false;
  let layerMaxExtent = 0;
  const globalSpace = viewer.coordinateSpace.value;
  for (const name of names) {
    const managedLayer = viewer.layerManager.getLayerByName(name);
    const userLayer = managedLayer?.layer;
    if (!userLayer) {
      continue;
    }
    const localSpace = userLayer.localCoordinateSpace?.value;
    if (localSpace === undefined || !localSpace.valid) {
      continue;
    }
    const {rank} = localSpace;
    const localCoords = new Float32Array(rank);
    getBoundingBoxCenter(localCoords, localSpace.bounds);
    for (let i = 0; i < rank; ++i) {
      localCoords[i] = Math.floor(localCoords[i]) + 0.5;
    }
    const localPosition = userLayer.localPosition;
    if (localPosition === null) {
      continue;
    }
    localPosition.value = localCoords;
    for (let i = 0; i < rank; ++i) {
      layerMaxExtent = Math.max(
          layerMaxExtent,
          localSpace.bounds.upperBounds[i] - localSpace.bounds.lowerBounds[i]);
    }
    if (globalSpace.valid && globalSpace.rank === rank) {
      viewer.position.value = localCoords;
      centeredFromLayer = true;
      break;
    }
  }

  if (!centeredFromLayer) {
    const coordinateSpace = viewer.coordinateSpace.value;
    if (coordinateSpace.valid) {
      const coords = new Float32Array(coordinateSpace.rank);
      getBoundingBoxCenter(coords, coordinateSpace.bounds);
      for (let i = 0; i < coordinateSpace.rank; ++i) {
        coords[i] = Math.floor(coords[i]) + 0.5;
      }
      viewer.position.value = coords;
    }
  }

  if (payload.projectionScale !== undefined) {
    setCamera(viewer, payload);
    return;
  }

  if (payload.projectionScale === undefined) {
    let maxExtent = 0;
    if (centeredFromLayer && layerMaxExtent > 0) {
      maxExtent = layerMaxExtent;
    } else if (names.length === 0) {
      const coordinateSpace = viewer.coordinateSpace.value;
      if (coordinateSpace.valid) {
        for (let i = 0; i < coordinateSpace.rank; ++i) {
          maxExtent = Math.max(
              maxExtent,
              coordinateSpace.bounds.upperBounds[i] - coordinateSpace.bounds.lowerBounds[i]);
        }
      }
    }
    if (maxExtent > 0) {
      viewer.projectionScale.restoreState(Math.max(500, maxExtent * 1.15));
    }
  }
}

type PortalLayerSpec = {
  visible?: boolean;
  segments?: string[];
  segmentDefaultColor?: string;
  segmentColors?: Record<string, string>;
};

/**
 * Same switch as Neuroglancer's perspective-view "Sections" checkbox
 * (`showPerspectiveSliceViews` / the `showSlices` state key).
 */
function setViewerSections(viewer: Viewer, payload: Record<string, unknown>) {
  const enabled = payload.enabled === true;
  if (viewer.showPerspectiveSliceViews.value !== enabled) {
    viewer.showPerspectiveSliceViews.toggle();
  }
  const rawLayer = viewer.layerManager.getLayerByName('lsm_raw_volume');
  if (rawLayer !== undefined) {
    rawLayer.setVisible(enabled);
  }
}

function applyInitialState(viewer: Viewer, payload: Record<string, unknown>) {
  const layers = payload.layers;
  if (!layers || typeof layers !== 'object') {
    return;
  }

  for (const [name, raw] of Object.entries(layers as Record<string, PortalLayerSpec>)) {
    const managedLayer = viewer.layerManager.getLayerByName(name);
    if (managedLayer === undefined) {
      continue;
    }
    if (typeof raw.visible === 'boolean') {
      managedLayer.setVisible(raw.visible);
    }

    const userLayer = managedLayer.layer;
    if (!(userLayer instanceof SegmentationUserLayer)) {
      continue;
    }

    if (Array.isArray(raw.segments)) {
      const groupState = userLayer.displayState.segmentationGroupState.value;
      groupState.visibleSegments.clear();
      for (const idStr of raw.segments) {
        const id = Uint64.parseString(String(idStr), 10);
        groupState.visibleSegments.add(groupState.segmentEquivalences.get(id));
      }
    }

    const colorSpec: Record<string, unknown> = {};
    if (raw.segmentDefaultColor !== undefined) {
      colorSpec.segmentDefaultColor = raw.segmentDefaultColor;
    }
    if (raw.segmentColors !== undefined) {
      colorSpec.segmentColors = raw.segmentColors;
    }
    if (Object.keys(colorSpec).length > 0) {
      userLayer.displayState.segmentationColorGroupState.value.restoreState(colorSpec);
    } else if (Array.isArray(raw.segments)) {
      const colorGroup = userLayer.displayState.segmentationColorGroupState.value;
      colorGroup.segmentDefaultColor.value = undefined;
      colorGroup.segmentStatedColors.clear();
    }
  }

  if (payload.clippingPlane !== undefined) {
    const clip = Number(payload.clippingPlane);
    if (Number.isFinite(clip) && clip > 0) {
      viewer.projectionDepthRange.value = -clip;
    } else {
      viewer.projectionDepthRange.reset();
    }
  }
}

/**
 * Listens for portal postMessage commands when neuroglancer is embedded in an iframe.
 */
export function registerParentCommandBridge(viewer: Viewer) {
  window.addEventListener('message', (event: MessageEvent) => {
    const data = event.data as ParentCommandMessage;
    if (!data || data.type !== 'NEUROSIMP_COMMAND') {
      return;
    }

    const payload = data.payload ?? {};

    switch (data.command) {
      case 'SIMULATE_DRAG':
        simulateDrag(
            viewer, Number(payload.deltaX) || 0, Number(payload.deltaY) || 0);
        break;
      case 'SET_CAMERA':
        setCamera(viewer, payload);
        break;
      case 'SET_LAYER_VISIBILITY':
        setLayerVisibility(viewer, payload);
        break;
      case 'SET_PROJECTION_DEPTH':
        setProjectionDepth(viewer, payload);
        break;
      case 'UPDATE_CLIPPING_PLANE':
        updateClippingPlane(viewer, payload);
        break;
      case 'INITIAL_STATE':
        applyInitialState(viewer, payload);
        break;
      case 'CENTER_ON_LAYERS':
        centerOnLayers(viewer, payload);
        break;
      case 'FOCUS_LAYER':
        focusLayer(viewer, payload);
        break;
      case 'SET_VIEWER_SECTIONS':
        setViewerSections(viewer, payload);
        break;
      default:
        break;
    }
  });
}
